import { isStoryBibleCtaType } from "~shared/agenticChat/storyBibleCtaTypes";
import type { CTAAction } from "src/components/PersistentChat/ThinkingStep/cta-types";
import {
  STORY_BIBLE_CTA_PROGRESS,
  computeCastApplyProgress,
  mergeStoryBibleCtaProgress,
  parseCastGeneratedCountFromToolSummary,
  toolNameMatchesStoryBibleCta,
} from "src/components/PersistentChat/storyBibleCtaProgress";
import { publishStoryBibleEditedForCTAContentUpdate } from "src/components/PersistentChat/ctaAnalytics";

export const STORY_BIBLE_CTA_PROGRESS_RESET = {
  storyBibleCtaProgress: 0,
  storyBibleCtaToolStartedAt: null as number | null,
  storyBibleCtaCastAppliedCount: 0,
  storyBibleCtaCastExpectedCount: null as number | null,
};

export type StoryBibleCtaProgressSlice = typeof STORY_BIBLE_CTA_PROGRESS_RESET;

type StoreGet = () => {
  ctaAction: CTAAction | null;
  storyBibleCtaProgress: number;
  storyBibleCtaCastAppliedCount: number;
  storyBibleCtaCastExpectedCount: number | null;
};

type StoreSet = (
  partial:
    | Partial<StoryBibleCtaProgressSlice & { storyBibleCtaProgress: number }>
    | ((state: StoreGet extends () => infer S ? S : never) => Partial<StoryBibleCtaProgressSlice & { storyBibleCtaProgress: number }>),
) => void;

export function isActiveStoryBibleCtaRun(get: StoreGet): boolean {
  const cta = get().ctaAction;
  return Boolean(cta && isStoryBibleCtaType(cta.type) && cta.status === "in_progress");
}

export function bumpStoryBibleCtaProgress(
  get: StoreGet,
  set: StoreSet,
  next: number,
  extra?: Partial<StoryBibleCtaProgressSlice>,
): void {
  if (!isActiveStoryBibleCtaRun(get)) return;
  set((state) => ({
    storyBibleCtaProgress: mergeStoryBibleCtaProgress(state.storyBibleCtaProgress, next),
    ...extra,
  }));
}

export function onStoryBibleCtaToolStart(get: StoreGet, set: StoreSet, toolName: string): void {
  if (!isActiveStoryBibleCtaRun(get)) return;
  const cta = get().ctaAction!;
  if (!toolNameMatchesStoryBibleCta(cta.type, toolName)) return;
  bumpStoryBibleCtaProgress(get, set, STORY_BIBLE_CTA_PROGRESS.toolRunning, {
    storyBibleCtaToolStartedAt: Date.now(),
  });
}

export function onStoryBibleCtaToolComplete(
  get: StoreGet,
  set: StoreSet,
  toolName: string,
  summary: string | undefined,
): void {
  if (!isActiveStoryBibleCtaRun(get)) return;
  const cta = get().ctaAction!;
  if (!toolNameMatchesStoryBibleCta(cta.type, toolName)) return;
  if (cta.type === "generate_cast" && toolName === "generate_cast") {
    const expected = parseCastGeneratedCountFromToolSummary(summary);
    bumpStoryBibleCtaProgress(get, set, STORY_BIBLE_CTA_PROGRESS.toolComplete, {
      storyBibleCtaCastExpectedCount: expected,
      storyBibleCtaCastAppliedCount: 0,
    });
    return;
  }
  bumpStoryBibleCtaProgress(get, set, STORY_BIBLE_CTA_PROGRESS.toolComplete, {
    storyBibleCtaToolStartedAt: null,
  });
}

export function onStoryBibleCtaContentUpdate(get: StoreGet, set: StoreSet): void {
  const cta = get().ctaAction;
  if (!cta || !isStoryBibleCtaType(cta.type) || cta.status !== "in_progress") return;

  if (cta.type === "generate_cast") {
    const appliedCount = get().storyBibleCtaCastAppliedCount + 1;
    const expected = get().storyBibleCtaCastExpectedCount;
    bumpStoryBibleCtaProgress(get, set, computeCastApplyProgress(appliedCount, expected), {
      storyBibleCtaCastAppliedCount: appliedCount,
      storyBibleCtaToolStartedAt: null,
    });
    return;
  }

  publishStoryBibleEditedForCTAContentUpdate(cta.type);

  bumpStoryBibleCtaProgress(get, set, STORY_BIBLE_CTA_PROGRESS.contentApplied, {
    storyBibleCtaToolStartedAt: null,
  });
}

export function onStoryBibleCtaEntityGenerated(get: StoreGet, set: StoreSet): void {
  bumpStoryBibleCtaProgress(get, set, STORY_BIBLE_CTA_PROGRESS.entityReady, {
    storyBibleCtaToolStartedAt: null,
  });
}

export function storyBibleCtaProgressFieldsForSetCtaAction(action: CTAAction | null): StoryBibleCtaProgressSlice {
  if (action?.status === "in_progress" && isStoryBibleCtaType(action.type)) {
    return {
      storyBibleCtaProgress: STORY_BIBLE_CTA_PROGRESS.started,
      storyBibleCtaToolStartedAt: null,
      storyBibleCtaCastAppliedCount: 0,
      storyBibleCtaCastExpectedCount: null,
    };
  }
  if (!action || action.status !== "in_progress") {
    return { ...STORY_BIBLE_CTA_PROGRESS_RESET };
  }
  return STORY_BIBLE_CTA_PROGRESS_RESET;
}
